import { DateObject, type InitFlowComponent, useSettings } from '@capsicum/common';
import { useMe } from '@capsicum/socios-core';
import { type ComponentProps, type ComponentType, useCallback, useEffect, useMemo, useState } from 'react';
import { match } from 'ts-pattern';

import { type InitFlowPageProps } from '../models';

type UseNCWInitFlowProps = Pick<ComponentProps<InitFlowComponent>, 'fallback' | 'isReactive' | 'next'> & {
	isVisible?: boolean;
	NCWExistingUserOnboardingFlow: ComponentType<InitFlowPageProps>;
	NCWNewUserOnboardingFlow: ComponentType<InitFlowPageProps>;
};

export const useNCWInitFlow = ({
	fallback,
	isReactive,
	isVisible,
	NCWExistingUserOnboardingFlow,
	NCWNewUserOnboardingFlow,
	next
}: UseNCWInitFlowProps) => {
	const { isMeLoading, me } = useMe({ enabled: !!isVisible });
	const [variant, setVariant] = useState<'EXISTING_USER_ONBOARDING' | 'HIDDEN' | 'NEW_USER_ONBOARDING'>();

	const {
		areSettingsLoading,
		settings: { poblanoReleaseDate }
	} = useSettings();

	const isLoading = isMeLoading || areSettingsLoading || isVisible === undefined;

	const userRegistrationDate = useMemo(() => {
		return !me.createdAt ? null : new DateObject(me.createdAt);
	}, [me.createdAt]);

	const eligibilityDate = useMemo(() => {
		return !poblanoReleaseDate ? null : new DateObject(poblanoReleaseDate);
	}, [poblanoReleaseDate]);

	const done = useCallback(() => setVariant('HIDDEN'), []);

	useEffect(() => {
		if (!isReactive || isLoading) return;

		if (!isVisible || !eligibilityDate || !userRegistrationDate) {
			done();

			return;
		}

		setVariant(eligibilityDate.isBefore(userRegistrationDate) ? 'NEW_USER_ONBOARDING' : 'EXISTING_USER_ONBOARDING');
	}, [done, isLoading, isReactive, isVisible, eligibilityDate, userRegistrationDate]);

	useEffect(() => {
		if (!isReactive) next();
	}, [next, isReactive]);

	if (!isReactive || variant === 'HIDDEN') return fallback;

	return match(variant)
		.with('NEW_USER_ONBOARDING', () => <NCWNewUserOnboardingFlow done={done} />)
		.with('EXISTING_USER_ONBOARDING', () => <NCWExistingUserOnboardingFlow done={done} />)
		.otherwise(() => null);
};
